import { useMemo, useState } from "react";
import { clsx } from "clsx";
import {
  AlertTriangle,
  Check,
  Edit2,
  Plus,
  Search,
  Send,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useMarkAnnouncementRead,
  usePublishAnnouncement,
  useUpdateAnnouncement,
} from "../hooks/useWhatsNew";
import { AnnouncementFormModal } from "../components/AnnouncementFormModal";
import { AnnouncementBody } from "../components/AnnouncementBody";
import { AnnouncementAudience } from "../components/AnnouncementAudience";
import { PublishAnnouncementModal } from "../components/PublishAnnouncementModal";
import { ANNOUNCEMENT_ROLES, sortAnnouncements } from "../types";
import type { Announcement, AnnouncementFormData } from "../types";

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function rolesLabel(roles: string[]): string {
  const labels = ANNOUNCEMENT_ROLES.filter((r) => roles.includes(r.value)).map((r) => r.label);
  return labels.length ? labels.join(", ") : "brak odbiorców";
}

// Diacritic-insensitive lowercase, so „wypisów" matches „wypisow" etc.
const normalize = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ł/g, "l");

/** The date an announcement is filtered on: publication date, or creation for drafts. */
function announcementDate(a: Announcement): string {
  return (a.published_at ?? a.created_at ?? "").slice(0, 10); // yyyy-mm-dd
}

interface Filters {
  query: string;
  from: string;
  to: string;
}

function matchesFilters(a: Announcement, f: Filters): boolean {
  if (f.query) {
    const q = normalize(f.query);
    if (!normalize(`${a.title}\n${a.body}`).includes(q)) return false;
  }
  const d = announcementDate(a);
  if (f.from && d && d < f.from) return false;
  if (f.to && d && d > f.to) return false;
  return true;
}

interface CardProps {
  announcement: Announcement;
  isManager: boolean;
  canSeeAudience: boolean;
  onEdit: (a: Announcement) => void;
  onPublish: (a: Announcement) => void;
  onDelete: (a: Announcement) => void;
  onMarkRead: (a: Announcement) => void;
}

function AnnouncementCard({
  announcement: a,
  isManager,
  canSeeAudience,
  onEdit,
  onPublish,
  onDelete,
  onMarkRead,
}: CardProps) {
  const isPublished = a.status === "published";
  const unread = isPublished && !a.is_read;
  // Important announcements stay visually flagged; the ring only pulses while unread.
  const pulsing = a.is_important && unread;

  return (
    <article
      className={clsx(
        "border rounded-lg p-4",
        a.is_important
          ? "border-red-400 dark:border-red-500 bg-red-50/60 dark:bg-red-950/20"
          : unread
            ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5"
            : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900",
        pulsing && "animate-important-pulse",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            {unread && <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] shrink-0" />}
            {a.title}
            {a.is_important && (
              <span
                className={clsx(
                  "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0",
                  "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300",
                  pulsing && "animate-pulse",
                )}
              >
                <AlertTriangle size={11} /> Ważne
              </span>
            )}
            {a.status === "proposed" && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                Proponowany
              </span>
            )}
            {a.is_deployment && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                Wdrożenie
              </span>
            )}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {isPublished
              ? `Opublikowano ${formatDate(a.published_at)}`
              : `Utworzono ${formatDate(a.created_at)}`}
            {isManager && ` · Odbiorcy: ${rolesLabel(a.allowed_roles)}`}
          </p>
        </div>

        {isManager && (
          <div className="flex items-center gap-1 shrink-0">
            {a.status === "proposed" && (
              <button
                onClick={() => onPublish(a)}
                title="Publikuj"
                className="p-1.5 rounded text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
              >
                <Send size={15} />
              </button>
            )}
            <button
              onClick={() => onEdit(a)}
              title="Edytuj"
              className="p-1.5 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Edit2 size={15} />
            </button>
            <button
              onClick={() => onDelete(a)}
              title="Usuń"
              className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </div>

      {a.body && <AnnouncementBody text={a.body} className="mt-3" />}

      {unread && (
        <div className="mt-3">
          <button
            onClick={() => onMarkRead(a)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Check size={13} /> Oznacz jako przeczytane
          </button>
        </div>
      )}

      {canSeeAudience && (
        <AnnouncementAudience announcementId={a.id} isPublished={isPublished} />
      )}
    </article>
  );
}

export function WhatsNewPage() {
  const user = useAuthStore((s) => s.user);
  const isManager = !!(user?.is_developer || user?.role === "director");

  const { data: announcements = [], isLoading } = useAnnouncements();
  const createMut = useCreateAnnouncement();
  const updateMut = useUpdateAnnouncement();
  const publishMut = usePublishAnnouncement();
  const deleteMut = useDeleteAnnouncement();
  const markReadMut = useMarkAnnouncementRead();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [publishing, setPublishing] = useState<Announcement | null>(null);
  const [filters, setFilters] = useState<Filters>({ query: "", from: "", to: "" });
  const hasFilters = !!(filters.query || filters.from || filters.to);

  const { proposed, published } = useMemo(() => {
    const visible = announcements.filter((a) => matchesFilters(a, filters));
    return {
      proposed: visible.filter((a) => a.status === "proposed"),
      // Important ones pinned on top (the backend orders the same way).
      published: sortAnnouncements(visible.filter((a) => a.status === "published")),
    };
  }, [announcements, filters]);

  const handleSave = async (data: AnnouncementFormData) => {
    if (editing) {
      await updateMut.mutateAsync({ id: editing.id, data });
    } else {
      await createMut.mutateAsync(data);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (a: Announcement) => {
    setEditing(a);
    setFormOpen(true);
  };

  const cardProps = {
    isManager,
    canSeeAudience: isManager || !!user?.is_ceo,
    onEdit: openEdit,
    // Publishing goes through a confirmation step where the manager decides
    // whether the announcement is important.
    onPublish: (a: Announcement) => setPublishing(a),
    onDelete: (a: Announcement) => {
      if (window.confirm(`Usunąć komunikat „${a.title}"?`)) deleteMut.mutate(a.id);
    },
    onMarkRead: (a: Announcement) => markReadMut.mutate(a.id),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Sparkles size={20} /> Co nowego
        </h1>
        {isManager && (
          <button
            onClick={openNew}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={15} /> Nowy komunikat
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={15}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            type="text"
            value={filters.query}
            onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            placeholder="Szukaj w tytule i treści…"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg pl-8 pr-3 py-2 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <label className="flex flex-col text-[11px] text-gray-500 dark:text-gray-400">
          Od
          <DatePicker
            value={filters.from}
            max={filters.to || undefined}
            onChange={(v) => setFilters((f) => ({ ...f, from: v }))}
          />
        </label>
        <label className="flex flex-col text-[11px] text-gray-500 dark:text-gray-400">
          Do
          <DatePicker
            value={filters.to}
            min={filters.from || undefined}
            onChange={(v) => setFilters((f) => ({ ...f, to: v }))}
          />
        </label>
        {hasFilters && (
          <button
            onClick={() => setFilters({ query: "", from: "", to: "" })}
            title="Wyczyść filtry"
            className="inline-flex items-center gap-1 text-xs px-2.5 py-2 text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <X size={14} /> Wyczyść
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-8">
          {isManager && (
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Proponowane (widoczne tylko dla Ciebie)
              </h2>
              {proposed.length === 0 ? (
                <p className="text-sm text-gray-400">Brak propozycji.</p>
              ) : (
                <div className="space-y-3">
                  {proposed.map((a) => (
                    <AnnouncementCard key={a.id} announcement={a} {...cardProps} />
                  ))}
                </div>
              )}
            </section>
          )}

          <section>
            {isManager && (
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Opublikowane
              </h2>
            )}
            {published.length === 0 ? (
              <p className="text-sm text-gray-400">Brak komunikatów.</p>
            ) : (
              <div className="space-y-3">
                {published.map((a) => (
                  <AnnouncementCard key={a.id} announcement={a} {...cardProps} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {formOpen && (
        <AnnouncementFormModal
          announcement={editing}
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
      )}

      {publishing && (
        <PublishAnnouncementModal
          announcement={publishing}
          onPublish={(isImportant) =>
            publishMut.mutateAsync({ id: publishing.id, isImportant })
          }
          onClose={() => setPublishing(null)}
        />
      )}
    </div>
  );
}
